import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useLocalization } from '@ui-framework/index';
import { useState } from 'react';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import { getWeekday, IconLibrary } from '@product-framework/index';
import { useFormContext } from 'react-hook-form';
import CutOffDay from './CutOffDay';
import { CUTOFF_HOUR_RANGE } from '../../../utils/constants';
import lib from '../../../utils/lib';

function CutoffTimeDialog({
  onChange,
  name,
  selectedCutoffWeekday,
  setSelectedCutoffWeekday,
}) {
  const { getValues } = useFormContext();
  const { formatMessage } = useLocalization();

  const value = getValues(name);

  const [cutoffData, setCutOffData] = useState({
    weekday: selectedCutoffWeekday,
    daysBefore: value[selectedCutoffWeekday].cutoff?.daysBefore,
    time: value[selectedCutoffWeekday].cutoff?.time,
  });

  if (!selectedCutoffWeekday) {
    return null;
  }

  const acceptJobOnDay = (selectedCutoffWeekday, formatMessage)
    ? getWeekday({ formatMessage, weekDay: selectedCutoffWeekday }).longLabel
    : '';

  return (
    <Dialog
      open={Boolean(selectedCutoffWeekday)}
      onClose={() => setSelectedCutoffWeekday(null)}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.CutOffTime />
        <FormattedMessage id="CUTOFF_TIME" defaultMessage="Cutoff Time" />
        <DialogCloseButton onClose={() => setSelectedCutoffWeekday(null)} />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            <FormattedMessage
              id="TO_ACCEPT_JOBS_ON_ACCEPTJOBONDAY_MY_CUTOFF_TIME_IS"
              defaultMessage="To accept jobs on {acceptJobOnDay}, my cutoff time is"
              values={{ acceptJobOnDay }}
            />
            :
          </Typography>
          {selectedCutoffWeekday && (
            <CutOffDay
              value={cutoffData?.daysBefore}
              onChange={e => {
                setCutOffData({
                  ...cutoffData,
                  daysBefore: Number(e.target.value),
                  time: cutoffData?.time,
                });
              }}
              dayOfWeek={selectedCutoffWeekday}
            />
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ width: 104, flexShrink: 0 }}>
              <InputLabel id="cutoff-time-hour-label">
                <FormattedMessage
                  id="CUTOFF_TIME"
                  defaultMessage="Cutoff Time"
                />
              </InputLabel>
              <Select
                labelId="cutoff-time-hour-label"
                label="Cutoff Time"
                value={cutoffData?.time ?? 12}
                onChange={e => {
                  setCutOffData({
                    ...cutoffData,
                    daysBefore: cutoffData?.daysBefore,
                    time: e.target.value,
                  });
                }}>
                {CUTOFF_HOUR_RANGE.map(hour => (
                  <MenuItem
                    key={hour}
                    value={lib.formatCutoffHourOption24h(hour)}>
                    {lib.formatCutoffHourOption24h(hour)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ pt: 3 }}>
              <Typography variant="subtitle2" color="black">
                {lib.formatCutoffTimeDisplay(cutoffData.time)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
        <Button
          startIcon={<IconLibrary.Yes />}
          variant="contained"
          onClick={() => {
            onChange(cutoffData);
            setSelectedCutoffWeekday(null);
          }}>
          <FormattedMessage id="OK" defaultMessage="Ok" />
        </Button>
        <Button
          startIcon={<IconLibrary.Cancel />}
          variant="outlined"
          onClick={() => setSelectedCutoffWeekday(null)}>
          <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CutoffTimeDialog;
